import { Settings } from "./settings";

/**
 * Pixi UI Settings - Dimensions and constants for Pixi React UI components
 * Separate from main settings.js to keep UI-specific values organized
 */

export const PixiSettings = {
  // Base UI element sizes (will be scaled responsively)
  BUTTON_SIZE: 36, // Standard buttons (home, fullscreen, etc.) - set this to your desired button size
  POPUP_BUTTON_MAX_WIDTH: 114, // Popup buttons max width (increased for better padding)
  POPUP_BUTTON_MAX_HEIGHT: 50, // Popup buttons max height (reduced from 57)
  SETTINGS_SWITCH_MAX_WIDTH: 640, // Settings switch max width for consistent layout
  POPUP_WINDOW_MAX_WIDTH: 450, // Popup window max width (reduced from 500)
  POPUP_WINDOW_MAX_HEIGHT: 300, // Popup window max height (reduced from 300)
  POPUP_WINDOW_VERTICAL_MAX_WIDTH: 450, // Vertical popup window max width (same as horizontal)
  POPUP_WINDOW_VERTICAL_MAX_HEIGHT: 450, // Vertical popup window max height (maintains 773:927 ratio at 500px width)

  // Layout spacing
  TOOLBAR_PADDING: 6, // Toolbar top/bottom padding

  // Layout constraints
  MAX_TOOLBAR_WIDTH: 1920,
  MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER: 10, // Multiplier for mobile landscape max width (based on GRID_SYMBOL_WIDTH)
  TOOLBAR_FADE_WIDTH: 60,

  // Responsive breakpoints for layout calculations
  ASPECT_RATIO_BREAKPOINTS: {
    PHONE: 540, // Mobile devices: smaller dimension < 600px (covers mobile landscape)
    TABLET: 1024, // Tablet devices: smaller dimension < 1024px
    DESKTOP_MIN_WIDTH: 1367, // Desktop minimum width threshold (1366px and below = tablet)
    DESKTOP_ASPECT_RATIO: 1.56, // Desktop aspect ratio threshold (1684×1080 or 1225×786)
    SMALL_DESKTOP_MAX_WIDTH: 1920, // Small vs large desktop distinction (1367-1920 = small, 1921+ = large)
  },

  // Layout configurations for different device orientations and types
  // Uses GRID dimensions for stable UI layout regardless of actual symbol size
  LAYOUT_CONFIGS: {
    MOBILE_PORTRAIT: {
      paddingSides: 0,
      paddingTop: Settings.GRID_SYMBOL_HEIGHT * 4,
      paddingBottom: Settings.GRID_SYMBOL_HEIGHT * 3,
    },
    MOBILE_LANDSCAPE: {
      paddingSides: Settings.GRID_SYMBOL_WIDTH * 4.5,
      paddingTop: Settings.GRID_SYMBOL_HEIGHT * 1.05,
      paddingBottom: Settings.GRID_SYMBOL_HEIGHT * 1.05,
    },
    TABLET_PORTRAIT: {
      paddingSides: Settings.GRID_SYMBOL_WIDTH * 3,
      paddingTop: Settings.GRID_SYMBOL_HEIGHT * 6,
      paddingBottom: Settings.GRID_SYMBOL_HEIGHT,
    },
    TABLET_LANDSCAPE: {
      paddingSides: Settings.GRID_SYMBOL_WIDTH * 5,
      paddingTop: Settings.GRID_SYMBOL_HEIGHT * 2.25,
      paddingBottom: Settings.GRID_SYMBOL_HEIGHT * 2.25,
    },
    SMALL_DESKTOP: {
      paddingSides: Settings.GRID_SYMBOL_WIDTH * 4,
      paddingTop: Settings.GRID_SYMBOL_HEIGHT,
      paddingBottom: Settings.GRID_SYMBOL_HEIGHT,
    },
    DESKTOP: {
      paddingSides: Settings.GRID_SYMBOL_WIDTH * 1.95,
      paddingTop: Settings.GRID_SYMBOL_HEIGHT,
      paddingBottom: Settings.GRID_SYMBOL_HEIGHT,
    },
  },

  // Reelset-specific layout configs - copy of UI configs for manual fine-tuning
  // Uses actual symbol dimensions for scaling calculations
  REELSET_LAYOUT_CONFIGS: {
    MOBILE_PORTRAIT: {
      paddingSides: 0,
      paddingTop: Settings.SYMBOL_HEIGHT * 3.6,
      paddingBottom: Settings.SYMBOL_HEIGHT * 4,
    },
    MOBILE_LANDSCAPE: {
      paddingSides: Settings.SYMBOL_WIDTH * 5,
      paddingTop: Settings.SYMBOL_HEIGHT * 1.0,
      paddingBottom: Settings.SYMBOL_HEIGHT * 1.0,
    },
    TABLET_PORTRAIT: {
      paddingSides: Settings.SYMBOL_WIDTH * 3,
      paddingTop: Settings.SYMBOL_HEIGHT * 3.5,
      paddingBottom: Settings.SYMBOL_HEIGHT * 3.5,
    },
    TABLET_LANDSCAPE: {
      paddingSides: Settings.SYMBOL_WIDTH * 5,
      paddingTop: Settings.SYMBOL_HEIGHT,
      paddingBottom: Settings.SYMBOL_HEIGHT,
    },
    SMALL_DESKTOP: {
      paddingSides: Settings.SYMBOL_WIDTH * 4,
      paddingTop: Settings.SYMBOL_HEIGHT,
      paddingBottom: Settings.SYMBOL_HEIGHT,
    },
    DESKTOP: {
      paddingSides: Settings.SYMBOL_WIDTH * 1.5,
      paddingTop: Settings.SYMBOL_HEIGHT,
      paddingBottom: Settings.SYMBOL_HEIGHT,
    },
  },

  DIVIDER_MAX_WIDTH: 4,

  // Layout type constants
  LAYOUT_TYPES: {
    MOBILE_PORTRAIT: "MOBILE_PORTRAIT",
    MOBILE_LANDSCAPE: "MOBILE_LANDSCAPE",
    TABLET_PORTRAIT: "TABLET_PORTRAIT",
    TABLET_LANDSCAPE: "TABLET_LANDSCAPE",
    SMALL_DESKTOP: "SMALL_DESKTOP",
    DESKTOP: "DESKTOP",
  },

  ORIENTATION: {
    PORTRAIT: "PORTRAIT",
    LANDSCAPE: "LANDSCAPE",
  },

  DEVICE: {
    MOBILE: "MOBILE",
    TABLET: "TABLET",
    DESKTOP: "DESKTOP",
  },

  // Device detection thresholds
  DEVICE_DETECTION: {
    SURFACE_DUO_ASPECT_RATIO: 2.5, // Minimum aspect ratio for Surface Duo/foldable detection
    SURFACE_DUO_MIN_DIMENSION: 400, // Minimum dimension for Surface Duo/foldable detection
    LARGE_MOBILE_WIDTH: 900, // Width threshold for large mobile devices
    LARGE_MOBILE_MIN_DIMENSION: 600, // Minimum dimension for large mobile devices
    SQUARE_DEVICE_TOLERANCE: 0.25, // Aspect ratio tolerance for square device detection (25% of 1.0)
    LANDSCAPE_ASPECT_RATIO: 1.1, // Minimum aspect ratio to be considered landscape
  },
};
